import { useState } from "react";
import { UI, FONT, T } from "../constants/tokens";
import { supabase } from "../lib/supabase";
import { restoreAccount, deletionSchedule, formatDate, GRACE_DAYS } from "../lib/accountService";
import LTR from "./LTR";

// ─── The grace window, days 1 to 30 ──────────────────────────────────────────
// Rendered instead of the app, not on top of it — see src/index.js. An account
// that is marked for deletion has no access, and a screen layered over a
// mounted App would still be loading that user's portfolio underneath.
//
// Content is dates and two actions. Nothing here asks why the user is leaving
// or offers them a reason to stay: the decision was made in Settings, the only
// question left is whether they still want it, and the way to answer that is a
// button, not persuasion.

export default function AccountDeletionPending({ requestedAt, onRestored }) {
  const [busy,  setBusy]  = useState(false);
  const [error, setError] = useState(null);

  const schedule = deletionSchedule(requestedAt);

  const restore = async () => {
    setBusy(true); setError(null);
    const res = await restoreAccount();
    if (res.ok) { onRestored?.(); return; }
    setError(res.status === 409
      ? `חלפו ${GRACE_DAYS} יום. לא ניתן לשחזר את החשבון.`
      : "השחזור לא הושלם. נסה שוב.");
    setBusy(false);
  };

  const btn = {
    width: "100%", minHeight: 48, borderRadius: T.radius.md, fontSize: 15,
    fontWeight: 800, cursor: busy ? "default" : "pointer", fontFamily: "inherit",
  };

  return (
    <div dir="rtl" style={{
      minHeight: "100dvh", background: UI.surface.page, color: UI.text.primary,
      fontFamily: FONT.body, display: "flex", alignItems: "center",
      justifyContent: "center", padding: 20,
    }}>
      <div style={{
        width: "min(480px, 100%)", background: UI.surface.raised,
        border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.lg,
        padding: "26px 22px", display: "flex", flexDirection: "column", gap: 16,
      }}>
        <div style={{ fontSize: 20, fontWeight: 800, fontFamily: FONT.heading }}>
          החשבון מסומן למחיקה
        </div>

        <div style={{ fontSize: 14.5, color: UI.text.secondary, lineHeight: 1.9 }}>
          <div>
            בקשת המחיקה נשלחה ב-
            <LTR style={{ fontFamily: FONT.latin, fontWeight: 700, color: UI.text.primary }}>{formatDate(schedule?.requestedAt)}</LTR>.
          </div>
          <div>
            הנתונים יימחקו סופית ב-
            <LTR style={{ fontFamily: FONT.latin, fontWeight: 700, color: UI.text.primary }}>{formatDate(schedule?.deletionAt)}</LTR>
            {" "}· בעוד <LTR>{schedule?.daysLeft ?? 0}</LTR> ימים.
          </div>
          <div>עד אז אין גישה לאפליקציה.</div>
        </div>

        {error && <div style={{ fontSize: 13.5, color: UI.text.secondary }}>{error}</div>}

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button
            onClick={restore}
            disabled={busy}
            style={{
              ...btn,
              background: UI.accent.soft,
              border: `1px solid ${UI.accent.base}`,
              color: UI.accent.base,
            }}>
            {busy ? "משחזר…" : "שחזור החשבון"}
          </button>
          <button
            onClick={() => supabase.auth.signOut()}
            disabled={busy}
            style={{
              ...btn,
              background: "transparent",
              border: `1px solid ${UI.line.hairline}`,
              color: UI.text.secondary,
              fontWeight: 700,
            }}>
            יציאה
          </button>
        </div>

        <div style={{ fontSize: 12, color: UI.text.muted, textAlign: "center" }}>
          מידע בלבד · אינו ייעוץ השקעות
        </div>
      </div>
    </div>
  );
}
